import { AxiosResponse } from "axios";
import { logger } from "../../../utilities/logger";
import { axiosRetryPost } from "../../../utilities/AxiosRetryUtil";
import authModule from "../../../utilities/AuthModule";
import { tryDecodeUriComponent } from "../../../utilities/EncodeDecode";

interface ICancellationToken {
    web: string;
    isCancellationRequested: boolean;
}

export class CancellableSharePointRestApi {
    private cancellationToken: ICancellationToken;

    constructor(webUrl: string) {
        this.cancellationToken = {
            web: webUrl,
            isCancellationRequested: false
        };
    }

    getIsCancellationRequested(webUrl: string): boolean {
        if (this.cancellationToken.web === webUrl) {
            return this.cancellationToken.isCancellationRequested;
        }
        return false;
    }

    cancel(webUrl: string): void {
        if (this.cancellationToken.web === webUrl) {
            this.cancellationToken.isCancellationRequested = true;
        }
    }

    async getSharepointLibraryRootItemCount(webUrl: string, sharePointListId: string): Promise<number> {
        try {
            const baseUrl = `${webUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream`;

            const requestConfig = {
                headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, accept: "application/json; odata=nometadata" }
            };

            const requestData = {
                parameters: {
                    DatesInUtc: true,
                    ViewXml: "<View><ViewFields><FieldRef Name=\"ID\"/></ViewFields><RowLimit Paged=\"TRUE\">5000</RowLimit></View>"
                }
            };

            let retry: boolean;
            let nextPageUrl: string;
            let response: AxiosResponse<any>;

            let itemCount: number = 0;
            let retryCount: number = 0;

            do {
                if (this.cancellationToken.isCancellationRequested || this.cancellationToken.web !== webUrl) {
                    break;
                }

                let url: string;
                if (nextPageUrl) {
                    url = baseUrl + nextPageUrl;
                } else {
                    url = baseUrl;
                }

                try {
                    response = await axiosRetryPost(url, requestData, requestConfig, false);

                    if (Array.isArray(response?.data?.Row) && response.data.Row.length > 0) {
                        itemCount += response.data.Row.length;
                    }

                    nextPageUrl = response?.data?.NextHref;

                    if (!nextPageUrl) {
                        break;
                    }
                } catch (error) {
                    // Retry throttling.
                    if (error?.response?.data["odata.error"]?.code?.startsWith("-2147024860") && ++retryCount < 3) {
                        retry = true;
                        await new Promise(res => setTimeout(res, 1000));
                    }
                    else {
                        break;
                    }
                }
            } while (
                !this.cancellationToken.isCancellationRequested
                && this.cancellationToken.web === webUrl
                && (retry || nextPageUrl)
            );

            return itemCount;

        } catch (err) {
            logger(err, "SharePointRestService.ts (getSharePointFiles)");
        }

        return 0;
    };

    async getSharepointFilterValues(webUrl: string, sharePointListId: string, folderServerRelativeUrl: string, filterField: string, filterValue: string): Promise<string[]> {
        try {
            const baseUrl = `${webUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream`;

            const requestConfig = {
                headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, accept: "application/json; odata=nometadata" }
            };

            const requestData = {
                parameters: {
                    DatesInUtc: true,
                    ViewXml: `<View><ViewFields><FieldRef Name="${filterField}"/></ViewFields><Query><Where><Contains id="${filterField}"><FieldRef Name="${filterField}"/><Value Type="Text">${filterValue}</Value></Contains></Where></Query><RowLimit Paged="TRUE">5000</RowLimit></View>`
                }
            };

            if (folderServerRelativeUrl) {
                requestData.parameters["FolderServerRelativeUrl"] = tryDecodeUriComponent(folderServerRelativeUrl);
            }

            let retry: boolean;
            let nextPageUrl: string;
            let response: AxiosResponse<any>;

            let retryCount: number = 0;

            const filterValues: string[] = [];

            do {
                if (this.cancellationToken.isCancellationRequested || this.cancellationToken.web !== webUrl) {
                    break;
                }

                let url: string;
                if (nextPageUrl) {
                    url = baseUrl + nextPageUrl;
                } else {
                    url = baseUrl;
                }

                try {
                    response = await axiosRetryPost(url, requestData, requestConfig, false);

                    if (Array.isArray(response?.data?.Row) && response.data.Row.length > 0) {
                        filterValues.push(...response.data.Row.map(item => item[filterField]));
                    }

                    nextPageUrl = response?.data?.NextHref;

                    if (!nextPageUrl) {
                        break;
                    }
                } catch (error) {
                    // Retry throttling.
                    if (error?.response?.data["odata.error"]?.code?.startsWith("-2147024860") && ++retryCount < 3) {
                        retry = true;
                        await new Promise(res => setTimeout(res, 1000));
                    }
                    else {
                        break;
                    }
                }
            } while (
                !this.cancellationToken.isCancellationRequested
                && this.cancellationToken.web === webUrl
                && (retry || nextPageUrl)
            );

            if (!this.cancellationToken.isCancellationRequested && this.cancellationToken.web === webUrl) {
                return filterValues.filter((v, i, a) => a.indexOf(v) === i);
            }

        } catch (err) {
            logger(err, "SharePointRestService.ts (getSharepointFilterValues)");
        }

        return [];
    };
}